import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Upload, Trash2, Eye, EyeOff, ImageIcon, X, Camera } from "lucide-react";
import { invalidateShowcaseCache } from "@/hooks/useShowcasePhotos";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminShowcasePage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");
  const [editingCaption, setEditingCaption] = useState(null);
  const [captionDraft, setCaptionDraft] = useState("");
  const fileInputRef = useRef(null);

  const showFeedback = (msg) => { setFeedback(msg); setError(""); setTimeout(() => setFeedback(""), 3000); };
  const showError = (msg) => { setError(msg); setFeedback(""); setTimeout(() => setError(""), 5000); };

  const fetchPhotos = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/showcase-photos`);
      setPhotos(data);
      invalidateShowcaseCache();
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { navigate("/"); return; }
    fetchPhotos();
  }, [user, authLoading, navigate, fetchPhotos]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let successCount = 0;
    let failCount = 0;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const { data: up } = await axios.post(`${API}/api/upload/image`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        await axios.post(`${API}/api/admin/showcase-photos`, { image_url: `/api/files/${up.path}` });
        successCount++;
      } catch (e) {
        console.error(e);
        failCount++;
      }
    }
    await fetchPhotos();
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (failCount === 0) showFeedback(`Uploaded ${successCount} photo${successCount === 1 ? "" : "s"}`);
    else showError(`Uploaded ${successCount}, ${failCount} failed`);
  };

  const toggleActive = async (photo) => {
    try {
      await axios.put(`${API}/api/admin/showcase-photos/${photo.id}`, { active: !photo.active });
      await fetchPhotos();
    } catch (e) { showError(e.response?.data?.detail || "Toggle failed"); }
  };

  const saveCaption = async (photo) => {
    try {
      await axios.put(`${API}/api/admin/showcase-photos/${photo.id}`, { caption: captionDraft });
      setEditingCaption(null);
      setCaptionDraft("");
      await fetchPhotos();
      showFeedback("Caption saved");
    } catch (e) { showError(e.response?.data?.detail || "Save failed"); }
  };

  const deletePhoto = async (photo) => {
    if (!window.confirm("Delete this photo? It will disappear from the homepage immediately.")) return;
    try {
      await axios.delete(`${API}/api/admin/showcase-photos/${photo.id}`);
      await fetchPhotos();
      showFeedback("Deleted");
    } catch (e) { showError(e.response?.data?.detail || "Delete failed"); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}><div className="w-8 h-8 border border-[#39FF14] border-t-transparent animate-spin" /></div>;

  const activeCount = photos.filter(p => p.active).length;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <button onClick={() => navigate("/admin")} data-testid="back-to-admin" className="flex items-center text-[#A1A1A1] hover:text-[#39FF14] font-mono text-xs uppercase tracking-wider mb-3">
          <ArrowLeft className="h-3 w-3 mr-2" /> Back to Admin
        </button>

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Camera className="h-5 w-5 text-[#39FF14]" strokeWidth={1.5} />
              <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14]">Admin</p>
            </div>
            <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[#F2F2F2]" style={{ fontFamily: "Chivo, sans-serif" }}>
              Photo Showcase
            </h1>
            <p className="text-[#A1A1A1] text-sm mt-2 max-w-2xl">
              Upload photos that appear as background art on the homepage. They cycle randomly every ~7 seconds so the page always feels alive.
              Only <span className="text-[#39FF14] font-bold">Active</span> photos are shown. {activeCount} of {photos.length} active.
            </p>
          </div>
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-showcase-btn"
            className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono text-xs uppercase tracking-wider disabled:opacity-50"
          >
            <Upload className="h-3 w-3 mr-2" />
            {uploading ? "Uploading…" : "Upload Photos"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => handleUpload(Array.from(e.target.files))}
            className="hidden"
            data-testid="showcase-file-input"
          />
        </div>

        {feedback && <div className="mb-6 p-3 border border-[#39FF14]/30 bg-[#39FF14]/5 text-[#39FF14] text-xs font-mono">{feedback}</div>}
        {error && <div className="mb-6 p-3 border border-red-500/30 bg-red-500/10 text-red-400 text-xs font-mono flex justify-between"><span>{error}</span><button onClick={() => setError("")}><X className="h-3 w-3" /></button></div>}

        {photos.length === 0 ? (
          <div className="bg-[#0D0D0D] border border-[#1A1A1A] p-16 text-center" data-testid="showcase-empty">
            <ImageIcon className="h-12 w-12 text-[#1A1A1A] mx-auto mb-4" strokeWidth={1} />
            <p className="text-[#A1A1A1] font-mono text-sm">No photos yet.</p>
            <p className="text-[#A1A1A1]/60 font-mono text-xs mt-2">Upload your first batch — pick as many as you like at once.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="showcase-grid">
            {photos.map((p) => (
              <div key={p.id} data-testid={`showcase-photo-${p.id}`} className={`relative group bg-[#0D0D0D] border ${p.active ? "border-[#1A1A1A]" : "border-yellow-500/40"} overflow-hidden`}>
                <div className="aspect-square overflow-hidden bg-[#050505] relative">
                  <img src={p.image_url} alt={p.caption || "showcase"} className={`w-full h-full object-cover transition-opacity ${p.active ? "opacity-100" : "opacity-40"}`} />
                  {!p.active && (
                    <div className="absolute top-2 left-2 bg-yellow-500/90 text-[#050505] text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 font-bold">
                      Hidden
                    </div>
                  )}
                </div>
                <div className="p-3">
                  {editingCaption === p.id ? (
                    <div className="space-y-2">
                      <Input
                        value={captionDraft}
                        onChange={(e) => setCaptionDraft(e.target.value)}
                        placeholder="Optional caption"
                        maxLength={80}
                        className="bg-[#050505] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono text-xs h-8"
                      />
                      <div className="flex gap-1">
                        <Button onClick={() => saveCaption(p)} className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono text-[10px] h-7 px-2 flex-1">Save</Button>
                        <Button onClick={() => { setEditingCaption(null); setCaptionDraft(""); }} variant="outline" className="bg-transparent border-[#1A1A1A] text-[#A1A1A1] rounded-none font-mono text-[10px] h-7 px-2">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <p
                      onClick={() => { setEditingCaption(p.id); setCaptionDraft(p.caption || ""); }}
                      className="font-mono text-[10px] text-[#A1A1A1] truncate cursor-pointer hover:text-[#39FF14]"
                      title="Click to edit caption"
                    >
                      {p.caption || "(no caption — click to add)"}
                    </p>
                  )}
                </div>
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => toggleActive(p)}
                    title={p.active ? "Hide from homepage" : "Show on homepage"}
                    data-testid={`toggle-active-${p.id}`}
                    className="w-7 h-7 bg-[#050505]/90 border border-[#1A1A1A] hover:border-[#39FF14] hover:text-[#39FF14] text-[#A1A1A1] flex items-center justify-center"
                  >
                    {p.active ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                  </button>
                  <button
                    onClick={() => deletePhoto(p)}
                    title="Delete"
                    data-testid={`delete-photo-${p.id}`}
                    className="w-7 h-7 bg-[#050505]/90 border border-[#1A1A1A] hover:border-red-500 hover:text-red-400 text-[#A1A1A1] flex items-center justify-center"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
